import React, { useState, useRef, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import './SearchableSelect.scss';

const SearchableSelect = ({ 
  options = [], 
  value = "", 
  onChange = () => {}, 
  placeholder = "Selecione...",
  searchPlaceholder = "Buscar...",
  disabled = false,
  className = "",
  required = false,
  name = "",
  renderOption = (option) => option.label || option,
  getOptionValue = (option) => option.value || option,
  getOptionLabel = (option) => option.label || option,
  maxHeight = "200px"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOption, setSelectedOption] = useState(null);
  const [dropdownAbove, setDropdownAbove] = useState(false);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Initialize selected option based on value
  useEffect(() => {
    if (value) {
      const option = options.find(opt => getOptionValue(opt) === value);
      setSelectedOption(option);
    } else {
      setSelectedOption(null);
    }
  }, [value, options]);

  // Calculate dropdown position to prevent overflow
  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownHeight = 240; // Approximate height of dropdown (search + options)
      
      // Check if there's enough space below
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      // Position dropdown above if not enough space below and enough space above
      if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
        setDropdownAbove(true);
      } else {
        setDropdownAbove(false);
      }
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Filter options based on search term
  const filteredOptions = options.filter(option => {
    const label = String(getOptionLabel(option)).toLowerCase();
    const optionValue = String(getOptionValue(option)).toLowerCase();
    const search = searchTerm.toLowerCase();
    return label.includes(search) || optionValue.includes(search);
  });

  const handleToggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        // Focus search input when opening
        setTimeout(() => {
          searchInputRef.current?.focus();
        }, 100);
      }
    }
  };

  const handleOptionClick = (option) => {
    const optionValue = getOptionValue(option);
    const isAlreadySelected = selectedOption && getOptionValue(selectedOption) === optionValue;
    
    // Allow deselection - if already selected, deselect it
    if (isAlreadySelected) {
      setSelectedOption(null);
      setIsOpen(false);
      setSearchTerm('');
      
      // Create synthetic event for deselection
      const syntheticEvent = {
        target: {
          name: name,
          value: ""
        },
        preventDefault: () => {}
      };
      
      onChange(syntheticEvent);
      return;
    }
    
    setSelectedOption(option);
    setIsOpen(false);
    setSearchTerm('');
    
    // Create synthetic event for compatibility with existing handlers
    const syntheticEvent = {
      target: {
        name: name,
        value: optionValue
      },
      preventDefault: () => {}
    };
    
    onChange(syntheticEvent);
  };

  const handleSearchChange = (e) => {
    e.stopPropagation();
    setSearchTerm(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  const handleSearchKeyDown = (e) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      e.preventDefault();
      // Select first filtered option if available
      if (filteredOptions.length > 0) {
        handleOptionClick(filteredOptions[0]);
      }
    }
  };

  return (
    <div className={`searchable-select ${className}`} ref={dropdownRef}>
      <div 
        className={`searchable-select-control ${disabled ? 'disabled' : ''} ${isOpen ? 'open' : ''} ${dropdownAbove ? 'dropdown-above' : ''}`}
        onClick={handleToggleDropdown}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
      >
        <div className="searchable-select-value">
          {selectedOption ? (
            <span className="selected-value">{getOptionLabel(selectedOption)}</span>
          ) : (
            <span className="placeholder">{placeholder}</span>
          )}
        </div>
        <div className="searchable-select-arrow">
          <i className={`bi ${isOpen ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
        </div>
      </div>

      {isOpen && (
        <div className={`searchable-select-dropdown ${dropdownAbove ? 'dropdown-above' : ''}`} style={{ maxHeight }}>
          <div className="searchable-select-search">
            <Form.Control
              ref={searchInputRef}
              type="text"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              className="search-input"
            />
          </div>
          <div className="searchable-select-options">
            {filteredOptions.length === 0 ? (
              <div className="no-options">Nenhuma opção encontrada</div>
            ) : (
              filteredOptions.map((option, index) => {
                const optionValue = getOptionValue(option);
                const isSelected = selectedOption && getOptionValue(selectedOption) === optionValue;
                
                return (
                  <div
                    key={index}
                    className={`searchable-select-option ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleOptionClick(option)}
                  >
                    {renderOption(option)}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
      
      {/* Hidden input for form compatibility */}
      <input
        type="hidden"
        name={name}
        value={selectedOption ? getOptionValue(selectedOption) : ''}
        required={required}
      />
    </div>
  );
};

export default SearchableSelect;
